import React from 'react'
import ReactDOM from 'react-dom'
import Header from '../common/Header'
import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ChevronLeft, ChevronRight, MoreVertical, User, Mail } from 'lucide-react'
import DeletePopUp from '../Modals/DeletePopUp'
import ActivatePopUp from '../Modals/ActivatePopUp'
import { getAllBrands, createBrand, updateBrand, deleteBrand, deleteBrandPermanent, activateBrand, getAgencyStats } from '../../requests'
import { deleteAuthToken } from '../../utils/helper'
import { useNavigate, useLocation } from 'react-router-dom'
import { removeUserInformation } from '../../_store/reducers/userReducer'
import { clearContext, setContext, mergeContext } from '../../_store/reducers/contextReducer'
import { notifyError, notifySuccess } from '../../utils/helper'
import Card from '../UI/Card'
import CardTitle from '../UI/CardTitle'
import KpiCard from '../UI/KpiCard'
import styles from '../../_css/Brand.module.css'
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer'
import useRole from '../../hooks/getRole'
import { toggleRefresh } from '../../_store/reducers/refreshReducer'


const PER_PAGE = 10

const Index = () => {
  const role = useRole()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const userInfo = useSelector((state) => state.user)
  const context = useSelector((state) => state.context)
  const [brands, setBrands] = useState([])
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: PER_PAGE,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  })
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({ brandName: "" })
  const [brandToDelete, setBrandToDelete] = useState(null)
  const [activateTarget, setActivateTarget] = useState(null)
  const [brandToDeletePermanent, setBrandToDeletePermanent] = useState(null)
  const [renameTarget, setRenameTarget] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameError, setRenameError] = useState(null)
  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [dropdownAnchor, setDropdownAnchor] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [brandStats, setBrandStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  })

  const resetAll = () => {
   setShowForm(false)
   setFormData({ brandName: "" })
   setSubmitError(null)
   setBrandToDelete(null)
   setActivateTarget(null)
   setRenameTarget(null)
  }

  useEffect(() => {
    resetAll()
  }, [userInfo?.agencyId])

  const fetchBrands = useCallback(async (page = 1) => {
    setLoading(true)
    try {
      const res = await getAllBrands({ page, limit: PER_PAGE })
      if (res?.status === 200) {
        const data = res?.data ?? res
        setBrands(Array.isArray(data?.data) ? data.data : [])
        if (data?.pagination) {
          setPagination(data.pagination)
        }
      }
    } catch (err) {
      //is status code 401, then redirect to login page
      if (err?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login')
        notifyError('Session expired. Please login again.' || err?.response?.data?.message)
      }
      else {
        setBrands([])
        console.error('Failed to fetch brands', err)
        notifyError(err?.response?.data?.message || 'Something went wrong')
      }
    } finally {
      setLoading(false)
    }
  }, [userInfo?.agencyId])

  const fetchBrandStats = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getAgencyStats()
      if (res?.status<400) {
        const data = res?.data ?? res
        setBrandStats(data?.brand)
      }
    } catch (err) {
      console.error('Failed to fetch brand stats', err)
      notifyError(err?.response?.data?.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }, [userInfo?.agencyId])

  useEffect(() => {
    fetchBrandStats()
  }, [fetchBrandStats])

  useEffect(() => {
    fetchBrands(pagination.currentPage)
  }, [pagination.currentPage, fetchBrands])

  useEffect(() => {
    if (location.state?.openAddForm) {
      setShowForm(true)
      setFormData({ brandName: '' })
    }
  }, [location.state])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError(null)
    const name = formData.brandName?.trim()
    if (!name) return
    if (name.length < 2) return setSubmitError('Brand name must be at least 2 characters long')
    if (name.length > 50) return setSubmitError('Brand name must be less than 50 characters long')

    dispatch(startLoader())
    try {
      const response = await createBrand({
        name,
        fullName: userInfo?.fullName ?? '',
        email: userInfo?.email ?? '',
      })
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Brand created successfully')
        setFormData({ brandName: "" })
        setShowForm(false)
        await fetchBrands(1)
        await fetchBrandStats()
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to save brand')
    } finally {
      dispatch(stopLoader())
    }
  }

  const handleChange = (field, value) => {
    setSubmitError(null)
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleDeleteClick = (brand) => {
    setBrandToDelete(brand)
  }

  const handleConfirmDelete = async () => {
    if (!brandToDelete?.id) return
    const id = brandToDelete.id
    try {
      const response = await deleteBrand(id)
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Brand deactivated successfully')
        setBrandToDelete(null)

        if(context.brand_id === id) {
          dispatch(clearContext())
        }
        await fetchBrands(pagination.currentPage)
        await fetchBrandStats()        
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to deactivate brand')
      setBrandToDelete(null)
    }
  }

  const handleDeletePermanentClick = (brand) => {
    setBrandToDeletePermanent(brand)
  }

  const handleConfirmDeletePermanent = async () => {
    if (role === 'user') return notifyError('You are not authorized to delete brands')
    if (!brandToDeletePermanent?.id) return
    const id = brandToDeletePermanent.id
    try {
      const response = await deleteBrandPermanent(id)
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Brand permanently deleted')
        setBrandToDeletePermanent(null)

        if(context.brand_id === id) {
          dispatch(clearContext())
        }
        await fetchBrands(pagination.currentPage)
        await fetchBrandStats()
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to delete brand')
      setBrandToDeletePermanent(null)
    }
  }

  const openActivateConfirm = (brand) => {
    setActivateTarget({ id: brand.id, name: brand.name })
  }

  const handleConfirmActivate = async () => {
    if (!activateTarget?.id) return
    const id = activateTarget.id
    try {
      const response = await activateBrand(id)
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Brand activated successfully')
        setActivateTarget(null)
        await fetchBrands(pagination.currentPage)
        await fetchBrandStats()
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to activate brand')
      setActivateTarget(null)
    }
  }

  const openRename = (brand) => {
    setRenameTarget({ id: brand.id, name: brand.name })
    setRenameValue(brand.name ?? '')
    setRenameError(null)
  }

  const closeRename = () => {
    setRenameTarget(null)
    setRenameValue('')
    setRenameError(null)
  }

  const handleRenameSubmit = async (e) => {
    e.preventDefault()
    if (!renameTarget?.id) return
    setRenameError(null)
    const name = renameValue?.trim()
    if (!name) return setRenameError('Please enter a brand name')
    if (name.length < 2) return setRenameError('Brand name must be at least 2 characters long')
    if (name.length > 50) return setRenameError('Brand name must be less than 50 characters long')
    if (name === renameTarget.name) return closeRename()

    const id = renameTarget.id
    dispatch(startLoader())
    try {
      const response = await updateBrand({ id, name })
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        if (context.brand_id === id) {
          dispatch(mergeContext({ brand_name: name }))
        }
        notifySuccess('Brand renamed successfully')
        closeRename()
        await fetchBrands(pagination.currentPage)
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to rename brand')
    } finally {
      dispatch(stopLoader())
    }
  }

  const handleReset = () => {
    setFormData({ brandName: '' })
    setSubmitError(null)
  } 

  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages) return
    setPagination((prev) => ({ ...prev, currentPage: page }))
  }

  const handleBrandRowClick = (brand) => {
    if (!brand?.id) return
    //is status is inactive the return and show a popup to activate the brand
    if (!brand.isActive) {
      notifyError('Please activate the brand to view the projects');
      return;
    }
    dispatch(
      setContext({
        brand_id: brand.id,
        brand_name: brand.name ?? '',
      })
    )

    dispatch(toggleRefresh());
    //agency id only if user is super admin
    if(role === 'super_admin') {
      navigate(`/projects?brand_id=${brand.id}&agency_id=${userInfo?.agencyId}`)
    } else {
      navigate(`/projects?brand_id=${brand.id}`)
    }
  }

  const activeOnPage = brands.filter((b) => b.isActive == true).length

  return (
    <div>
      {/* <Header userType={role} headerTitle="Brands" /> */}
      <div className={styles.page}>
        <div className={styles.headerRow}>
          <div className={styles.titleBlock}>
            <h1>Brands</h1>
            <p>Manage client brands</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowForm(!showForm)
              if (showForm) {
                setFormData({ brandName: '' })
                setSubmitError(null)
              }
            }}
            className={styles.primaryBtn}
          >
            {showForm ? '← Back to List' : '+ New Brand'}
          </button>
        </div>

        {showForm ? (
          <Card>
            <CardTitle>Create New Brand</CardTitle>
            <form onSubmit={handleSubmit}>
              {submitError && <p className={styles.formError}>{submitError}</p>}
              <div className={styles.formGrid}>
                <div className={styles.formField}>
                  <label htmlFor="brandName" className={styles.label}>
                    Brand Name <span className={styles.labelRequired}>*</span>
                  </label>
                  <input
                    id="brandName"
                    placeholder="e.g., E.ON"
                    value={formData.brandName}
                    onChange={(e) => handleChange('brandName', e.target.value)}
                    className={styles.input}
                    required
                    minLength={2}
                    maxLength={50}
                    aria-describedby="brandNameHelp"
                  />
                  <p id="brandNameHelp" className={styles.helpText}>
                    Enter the brand exactly as it is officially spelled (including capitalization and special characters).
                  </p>
                </div>
                <div className={styles.formField}>
                  <label htmlFor="brandFormEmail" className={styles.label}>
                    Email Address
                  </label>
                  <input
                    id="brandFormEmail"
                    type="email"
                    placeholder="e.g., info@eon.de"
                    value={userInfo?.email ?? ''}
                    readOnly
                    className={`${styles.input} ${styles.inputReadOnly}`}
                  />
                </div>
              </div>
              <div className={`${styles.formGrid} ${styles.formGridSingle}`}>
                <div className={styles.formField}>
                  <label htmlFor="brandFormFullName" className={styles.label}>
                    Full Name
                  </label>
                  <input
                    id="brandFormFullName"
                    placeholder="e.g., Anna Schmidt"
                    value={userInfo?.fullName ?? ''}
                    readOnly
                    className={`${styles.input} ${styles.inputReadOnly}`}
                  />
                </div>
                {/* <div>
                  <label className={styles.label}>Client Phone Number</label>
                  <input placeholder="e.g., +49 201 184-0" className={styles.input} readOnly disabled />
                </div> */}
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.submitBtn}>
                  💾 Save Brand
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className={styles.secondaryBtn}
                >
                  Reset
                </button>
              </div>
            </form>
          </Card>
        ) : (
          <>
            <div className={styles.kpiGrid}>
              <KpiCard label="Total Brands" value={brandStats?.total} sub={`${activeOnPage} active on this page`} icon="◎" />
              <KpiCard label="Active Brands" value={brandStats?.active} sub="Across all brands" icon="⚡" />
              <KpiCard label="Inactive Brands" value={brandStats?.inactive} sub="Across all brands" icon="⏳" />
            </div>
            {loading ? (
              <p className={styles.loadingText}>Loading brands...</p>
            ) : (
              <>
                <div className={styles.cardsList}>
                  {brands.length === 0 ? (
                    <div className={styles.listEmpty}>
                      No brands yet. Create one using the button above.
                    </div>
                  ) : (
                    brands.map((b) => {
                      const projectCount = b._count?.projects ?? 0
                      const simulationCount = b._count?.simulations ?? 0
                      const createdBy = b?.created_by?.full_name ?? '–'
                      const email = b?.created_by?.email ?? '–'
                      const isActive = b.isActive === true
                      return (
                        <Card key={b.id} style={{ cursor: 'pointer' }}>
                          <div className={styles.brandCard}>
                            <div
                              className={styles.brandCardMain}
                              onClick={() => handleBrandRowClick(b)}
                            >
                              <div className={styles.brandTitleRow}>
                                <div className={styles.brandName}>{b.name}</div>
                                <span
                                  className={`${styles.statusBadge} ${isActive ? styles.statusBadgeActive : styles.statusBadgeInactive}`}
                                >
                                  {isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              <div className={styles.brandMeta}>
                                <span className={styles.metaItem}>
                                  <User size={12} />
                                  {createdBy}
                                </span>
                                <span className={styles.metaItem}>
                                  <Mail size={12} />
                                  {email}
                                </span>
                              </div>
                            </div>
                            <div className={styles.cardRight}>
                              <div className={styles.statsGroup}>
                                <div className={styles.simBlock}>
                                  <div className={styles.simValue}>{projectCount}</div>
                                  <div className={styles.simLabel}>Projects</div>
                                </div>
                                <div className={styles.simBlock}>
                                  <div className={styles.simValue}>{simulationCount}</div>
                                  <div className={styles.simLabel}>Simulations</div>
                                </div>
                              </div>
                                <div className={styles.dropdownWrap}>
                                  <button
                                    type="button"
                                    className={styles.actionBtn}
                                    title="Actions"
                                    aria-haspopup="menu"
                                    aria-expanded={openDropdownId === b.id}
                                    aria-label="Actions"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      const rect = e.currentTarget.getBoundingClientRect()
                                      if (openDropdownId === b.id) {
                                        setOpenDropdownId(null)
                                        setDropdownAnchor(null)
                                      } else {
                                        setOpenDropdownId(b.id)
                                        setDropdownAnchor({
                                          top: rect.bottom,
                                          right: rect.right,
                                        })
                                      }
                                    }}
                                  >
                                    <MoreVertical size={18} />
                                  </button>
                                  {openDropdownId === b.id && dropdownAnchor &&
                                    ReactDOM.createPortal(
                                      <>
                                        <div
                                          className={styles.dropdownBackdrop}
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setOpenDropdownId(null)
                                            setDropdownAnchor(null)
                                          }}
                                          aria-hidden="true"
                                        />
                                        <ul
                                          className={styles.dropdownMenu}
                                          role="menu"
                                          style={{
                                            top: dropdownAnchor.top + 4,
                                            right:
                                              typeof window !== 'undefined'
                                                ? window.innerWidth - dropdownAnchor.right
                                                : 0,
                                          }}
                                        >
                                          <li role="none">
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className={styles.dropdownItem}
                                              onClick={() => {
                                                openRename(b)
                                                setOpenDropdownId(null)
                                                setDropdownAnchor(null)
                                              }}
                                            >
                                              Rename
                                            </button>
                                          </li>
                                          {b.isActive ? (
                                            <li role="none">
                                              <button
                                                type="button"
                                                role="menuitem"
                                                className={styles.dropdownItemDanger}
                                                onClick={() => {
                                                  handleDeleteClick(b)
                                                  setOpenDropdownId(null)
                                                  setDropdownAnchor(null)
                                                }}
                                              >
                                                Deactivate
                                              </button>
                                            </li>
                                          ) : (
                                            <li role="none">
                                              <button
                                                type="button"
                                                role="menuitem"
                                                className={styles.dropdownItem}
                                                onClick={() => {
                                                  openActivateConfirm(b)
                                                  setOpenDropdownId(null)
                                                  setDropdownAnchor(null)
                                                }}
                                              >
                                                Activate
                                              </button>
                                            </li>
                                          )}
                                          {role !== 'user' && (
                                            <li role="none">
                                              <button
                                                type="button"
                                                role="menuitem"
                                                className={styles.dropdownItemDanger}
                                                onClick={() => {
                                                  handleDeletePermanentClick(b)
                                                  setOpenDropdownId(null)
                                                  setDropdownAnchor(null)
                                                }}
                                              >
                                                Delete
                                              </button>
                                            </li>
                                          )}
                                        </ul>
                                      </>,
                                      document.body
                                    )}
                                </div>
                            </div>
                          </div>
                        </Card>
                      )
                    })
                  )}
                </div>
                <div className={styles.pagination}>
                  <span className={styles.paginationInfo}>
                    Showing {pagination.total === 0 ? 0 : (pagination.currentPage - 1) * pagination.perPage + 1}–{Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of {pagination.total}
                  </span>
                  {pagination.totalPages > 0 && (
                    <div className={styles.paginationBtns}>
                      <button
                        type="button"
                        onClick={() => goToPage(pagination.prevPage ?? pagination.currentPage - 1)}
                        disabled={!pagination.hasPrevPage}
                        className={styles.pageBtn}
                        title="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => goToPage(pagination.nextPage ?? pagination.currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        className={styles.pageBtn}
                        title="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {brandToDelete && (
        <DeletePopUp
          title="Deactivate brand"
          message={`Are you sure you want to deactivate "${brandToDelete.name}"? They will no longer have access.`}
          onClose={() => setBrandToDelete(null)}
          onDelete={handleConfirmDelete}
        />
      )}

      {activateTarget && (
        <ActivatePopUp
          title="Activate brand"
          message={`Are you sure you want to activate "${activateTarget.name}"?`}
          onClose={() => setActivateTarget(null)}
          onActivate={handleConfirmActivate}
        />
      )}

      {brandToDeletePermanent && (
        <DeletePopUp
          variant="delete"
          title="Delete brand"
          message={`This will permanently delete "${brandToDeletePermanent.name}" and all of its projects and simulations. This cannot be undone.`}
          onClose={() => setBrandToDeletePermanent(null)}
          onDelete={handleConfirmDeletePermanent}
        />
      )}

      {renameTarget && (
        <div
          className={styles.modalOverlay}
          onClick={closeRename}
          role="dialog"
          aria-modal="true"
          aria-label="Rename brand"
        >
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <Card>
              <CardTitle sub={`Current name: ${renameTarget.name}`}>Rename Brand</CardTitle>
              <form onSubmit={handleRenameSubmit}>
                {renameError && <p className={styles.formError}>{renameError}</p>}
                <div className={styles.formField}>
                  <label htmlFor="renameBrandName" className={styles.label}>
                    Brand Name <span className={styles.labelRequired}>*</span>
                  </label>
                  <input
                    id="renameBrandName"
                    value={renameValue}
                    onChange={(e) => {
                      setRenameError(null)
                      setRenameValue(e.target.value)
                    }}
                    className={styles.input}
                    required
                    minLength={2}
                    maxLength={50}
                    autoFocus
                    aria-describedby="renameBrandNameHelp"
                  />
                  <p id="renameBrandNameHelp" className={styles.helpText}>
                    Enter the brand exactly as it is officially spelled (including capitalization and special characters). Future runs of existing simulations use the new name.
                  </p>
                </div>
                <div className={styles.formActions}>
                  <button type="submit" className={styles.submitBtn}>
                    💾 Save
                  </button>
                  <button type="button" onClick={closeRename} className={styles.secondaryBtn}>
                    Cancel
                  </button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}

export default Index